import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Heart, MessageSquare, DollarSign, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DashboardMockup = () => {
  const { t } = useTranslation('auth');
  return (
  <div className="relative w-full max-w-[480px] mx-auto">
    <div className="bg-white/80 dark:bg-white/[0.03] backdrop-blur-xl border border-gray-200/80 dark:border-white/[0.06] rounded-2xl p-5 sm:p-6 shadow-xl dark:shadow-none" style={{ animation: 'cl-float 6s ease-in-out infinite' }}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-[10px] text-white font-bold">Y</div>
          <div>
            <div className="text-xs font-semibold text-gray-900 dark:text-white">{t('landing.hero.mockup.feedTitle', 'Your Feed')}</div>
            <div className="text-[10px] text-gray-500">{t('landing.hero.mockup.welcomeBack', 'Welcome back')}</div>
          </div>
        </div>
        <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/10 px-2 py-1 rounded-full">{t('landing.hero.mockup.newBadge', '+12 new')}</div>
      </div>
      <div className="mb-4">
        <div className="text-[10px] text-gray-500 mb-2">{t('landing.hero.mockup.newFromFollowing', 'New from creators you follow')}</div>
        <div className="grid grid-cols-4 gap-2">
          {['from-pink-400 to-rose-500', 'from-blue-400 to-cyan-500', 'from-amber-400 to-orange-500', 'from-purple-400 to-fuchsia-500'].map((g, i) => (
            <div key={i} className={`aspect-square rounded-xl bg-gradient-to-br ${g} relative overflow-hidden`}>
              {i === 1 && <Play className="w-4 h-4 text-white absolute inset-0 m-auto fill-white" />}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: t('landing.hero.mockup.following', 'Following'), value: '24', icon: Users },
          { label: t('landing.hero.mockup.unlocked', 'Unlocked'), value: '156', icon: Heart },
          { label: t('landing.hero.mockup.messages', 'Messages'), value: '12', icon: MessageSquare },
        ].map(({ label, value, icon: Icon }, i) => (
          <div key={i} className="bg-gray-50 dark:bg-white/[0.03] rounded-xl p-3 text-center border border-gray-100 dark:border-white/[0.04]">
            <Icon className="w-3.5 h-3.5 mx-auto mb-1.5" style={{ color: 'var(--color-primary, #10b981)' }} />
            <div className="text-sm font-bold text-gray-900 dark:text-white">{value}</div>
            <div className="text-[9px] text-gray-500">{label}</div>
          </div>
        ))}
      </div>
    </div>
    <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-8 bg-white/80 dark:bg-white/[0.03] backdrop-blur-xl border border-gray-200/80 dark:border-white/[0.06] rounded-xl p-3 w-48 sm:w-52 shadow-lg dark:shadow-none hidden sm:block" style={{ animation: 'cl-float2 5s ease-in-out infinite 0.5s' }}>
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0"><DollarSign className="w-4 h-4 text-white" /></div>
        <div>
          <div className="text-[10px] font-semibold text-gray-900 dark:text-white">{t('landing.hero.mockup.newContent', 'New content from Sarah')}</div>
          <div className="text-[9px] text-gray-500">{t('landing.hero.mockup.newContentDetail', '@sarah_j just posted')}</div>
        </div>
      </div>
    </div>
    <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-8 bg-white/80 dark:bg-white/[0.03] backdrop-blur-xl border border-gray-200/80 dark:border-white/[0.06] rounded-xl p-3 w-44 sm:w-48 shadow-lg dark:shadow-none hidden sm:block" style={{ animation: 'cl-float2 5s ease-in-out infinite 1.5s' }}>
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0 relative">
          <Play className="w-3.5 h-3.5 text-white fill-white" />
          <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse border border-white dark:border-[#0a0a0a]" />
        </div>
        <div>
          <div className="text-[10px] font-semibold text-gray-900 dark:text-white">{t('landing.hero.mockup.goingLive', 'Live Now')}</div>
          <div className="text-[9px] text-gray-500">{t('landing.hero.mockup.viewersWatching', 'Join 1.2K watching')}</div>
        </div>
      </div>
    </div>
  </div>
  );
};

const EN_DEFAULTS = {
  heading_line1: 'Get closer to the',
  heading_line2: 'creators',
  heading_line3: 'you love',
  subheading: 'Subscribe, chat, and unlock exclusive photos, videos, and livestreams from your favorite creators — all in one place.',
  cta_primary_text: 'Join Free',
  cta_secondary_text: 'Discover Creators',
};

const HeroBlock = ({ data = {} }) => {
  const { t } = useTranslation('auth');
  const d = {
    heading_line1: t('landing.hero.headingLine1', EN_DEFAULTS.heading_line1),
    heading_line2: t('landing.hero.headingLine2', EN_DEFAULTS.heading_line2),
    heading_line3: t('landing.hero.headingLine3', EN_DEFAULTS.heading_line3),
    subheading: t('landing.hero.subheading', EN_DEFAULTS.subheading),
    cta_primary_text: t('landing.hero.ctaPrimary', EN_DEFAULTS.cta_primary_text),
    cta_secondary_text: t('landing.hero.ctaSecondary', EN_DEFAULTS.cta_secondary_text),
  };

  return (
    <section className="relative min-h-screen flex items-center">
      <div className="absolute inset-0 overflow-hidden bg-white dark:bg-[#0a0a0a]" />
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-10 dark:opacity-15 blur-[150px] pointer-events-none"
        style={{ background: `radial-gradient(circle, var(--color-primary, #10b981) 0%, transparent 70%)` }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10 w-full pt-24 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h1 className="cl-serif cl-up text-[2.5rem] sm:text-[3.25rem] lg:text-[3.75rem] font-black leading-[1.05] tracking-[-0.02em] mb-6">
              <span>{d.heading_line1} </span>
              <span className="italic" style={{ color: 'var(--color-primary, #10b981)' }}>{d.heading_line2}</span>
              {d.heading_line3 && (<><br /><span>{d.heading_line3}</span></>)}
            </h1>
            <p className="cl-up cl-d1 text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed font-light max-w-lg">{d.subheading}</p>
            <div className="cl-up cl-d2 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="group inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-full font-semibold text-[15px] text-white dark:text-black bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 transition-all hover:-translate-y-0.5">
                {d.cta_primary_text}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:-scale-x-100" />
              </Link>
              <Link to="/login" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-[15px] text-gray-700 dark:text-white border border-gray-300 dark:border-white/15 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all hover:-translate-y-0.5">
                {d.cta_secondary_text}
              </Link>
            </div>
          </div>
          <div className="cl-up cl-d3 hidden lg:block"><DashboardMockup /></div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 opacity-30">
        <div className="w-6 h-10 border-2 border-gray-400 dark:border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2.5 bg-gray-400 dark:bg-white/60 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroBlock;
